import { fetchCart } from 'theme';
import { CartRuleEngine } from 'cart-rule-engine';

class PdpShippingBar extends HTMLElement {
	connectedCallback() {
		this.abortController = new AbortController();
		this.steps = [...this.querySelectorAll('[data-step]')];
		this.cartValueEl = this.querySelector('[data-cart-value]');
		this.remainingTextEl = this.querySelector('[data-remaining-text-target]');
		this.progressFillEl = this.querySelector('[data-progress-fill]');
		this.progressTrackEl = this.querySelector('[data-progress-track]');
		this.buildEngine();
		document.addEventListener(
			'cart:change',
			(event) => {
				this.renderVersion++;
				this.render(event.detail.cart);
			},
			{ signal: this.abortController.signal },
		);
		document.addEventListener(
			'ablyft:variant-ready',
			() => {
				const activeVariant = window.activeAbTestingIdentifier || null;
				if (activeVariant === this.builtWithVariant) return;
				this.buildEngine();
				this.init();
			},
			{ signal: this.abortController.signal },
		);
		this.init();
	}

	disconnectedCallback() {
		this.abortController.abort();
		this.renderVersion++;
	}

	buildEngine() {
		const settings = window.cartSettings || {};
		const activeVariant = window.activeAbTestingIdentifier || null;
		this.builtWithVariant = activeVariant;
		// Keep the cart configuration; Liquid selects shipping and gift steps for the PDP.
		this.engine = new CartRuleEngine({
			successMessage: settings.successMessage || '',
			rules: (settings.rules || []).filter((rule) => !rule.abTestVariant || rule.abTestVariant === activeVariant),
		});
	}

	async init() {
		const version = (this.renderVersion = (this.renderVersion || 0) + 1);
		try {
			const cart = await fetchCart;
			if (this.isConnected && version === this.renderVersion) this.render(cart);
		} catch (error) {
			console.error('Unable to initialize PDP shipping bar:', error);
		}
	}

	render(cart) {
		if (!cart) return;
		const state = this.getState(cart);
		this.hidden = state.rules.length === 0;
		if (this.hidden) return;

		this.renderCartValue(cart);
		this.renderSteps(state);
		this.renderProgress(cart, state);
		this.renderStatus(cart, state);
	}

	getState(cart) {
		this.engine.rules.forEach((rule) => {
			rule.isActive = rule.computeIsActive();
		});
		const stepIds = new Set(this.steps.map((step) => step.dataset.ruleId));
		const rules = this.engine.rules
			.filter((rule) => rule.isActive && stepIds.has(rule.ruleID))
			.sort((a, b) => a.threshold - b.threshold);
		const qualifiedIds = new Set(this.engine.getQualifiedRules(cart).map((rule) => rule.ruleID));
		const remainingRules = rules.filter((rule) => !qualifiedIds.has(rule.ruleID));
		return { rules, qualifiedIds, remainingRules, isSuccess: remainingRules.length === 0 };
	}

	renderCartValue(cart) {
		const moneyFormatter = new Intl.NumberFormat(Shopify.locale, {
			style: 'currency',
			currency: cart.currency || Shopify.currency.active,
		});
		this.cartValueEl.textContent = moneyFormatter.format(cart.total_price / 100);
	}

	renderSteps({ rules, qualifiedIds }) {
		this.steps.forEach((step) => {
			step.hidden = !rules.some((rule) => rule.ruleID === step.dataset.ruleId);
			step.classList.toggle('is-reached', qualifiedIds.has(step.dataset.ruleId));
		});
		const stepsContainer = this.querySelector('[data-steps]');
		rules.forEach((rule) => stepsContainer.appendChild(this.steps.find((step) => step.dataset.ruleId === rule.ruleID)));
	}

	renderProgress(cart, { rules, isSuccess }) {
		const maxThreshold = Math.max(...rules.map((rule) => rule.threshold));
		this.progressTrackEl.querySelectorAll('.pdp-shipping-bar__divider').forEach((divider) => divider.remove());
		const percent = isSuccess ? 100 : maxThreshold > 0 ? (cart.total_price / maxThreshold) * 100 : 0;
		this.progressFillEl.style.width = `${Math.max(0, Math.min(100, percent))}%`;
		[...new Set(rules.map((rule) => rule.threshold))]
			.filter((threshold) => threshold > 0 && threshold < maxThreshold)
			.forEach((threshold) => {
				const divider = document.createElement('span');
				divider.className = 'pdp-shipping-bar__divider';
				divider.style.left = `${(threshold / maxThreshold) * 100}%`;
				this.progressTrackEl.appendChild(divider);
			});
	}

	renderStatus(cart, { remainingRules, isSuccess }) {
		this.renderScheme(isSuccess);
		this.renderIcons(remainingRules[0]?.ruleID, isSuccess);
		this.renderText(cart, remainingRules, isSuccess);
	}

	renderScheme(isSuccess) {
		const scheme = isSuccess ? this.dataset.successSchemeClass : this.dataset.defaultSchemeClass;
		[this.dataset.defaultSchemeClass, this.dataset.successSchemeClass]
			.filter(Boolean)
			.forEach((name) => this.classList.remove(name));
		if (scheme) this.classList.add(scheme);
		this.classList.toggle('is-success', isSuccess);
	}

	renderIcons(nextRuleId, isSuccess) {
		this.querySelectorAll('[data-icon-for]').forEach((icon) => {
			icon.hidden = icon.dataset.iconFor !== nextRuleId;
		});
		this.querySelector('[data-icon-success]').hidden = !isSuccess;
	}

	renderText(cart, remainingRules, isSuccess) {
		this.remainingTextEl.innerHTML = isSuccess
			? this.engine.successMessage
			: remainingRules[0].getFormattedRemainingText({ cartTotal: cart.total_price });
	}
}

if (!window.customElements.get('pdp-shipping-bar')) {
	window.customElements.define('pdp-shipping-bar', PdpShippingBar);
}
